

import { Auth } from 'firebase/auth'; // Import Auth
import {
  FirebaseStorage,
  getDownloadURL,
  ref,
  uploadBytes,
  deleteObject,
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import type { ProcessedImage } from '@/lib/types';

type ImageReference = {
    path: string;
    url: string;
}

/**
 * Uploads an image to Firebase Storage and returns its download URL and storage path.
 *
 * @param storage - The FirebaseStorage instance.
 * @param auth - The FirebaseAuth instance.
 * @param file - The image file to upload.
 * @param novelId - The ID of the novel.
 * @param subfolder - A subfolder path within the novel's directory (e.g., 'locations' or 'character-images/characterId').
 * @returns A promise that resolves with the public URL and storage path of the uploaded image.
 */
export async function uploadImage(
  storage: FirebaseStorage,
  auth: Auth,
  file: File | Blob,
  novelId: string,
  subfolder: string
): Promise<ImageReference> {
  if (!file) {
    throw new Error('No file provided for upload.');
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to upload files.');
  }
  const userId = user.uid;

  let fileName = (file as File).name;
  if (!fileName) {
    const ext = file.type === 'image/webp' ? 'webp' : 'png';
    fileName = `image.${ext}`;
  }
  const uniqueFileName = `${uuidv4()}-${fileName}`;
  // New path structure includes the user's ID for robust security rules
  const storagePath = `users/${userId}/novels/${novelId}/${subfolder}/${uniqueFileName}`;
  const storageRef = ref(storage, storagePath);

  try {
    const snapshot = await uploadBytes(storageRef, file);

    // Instead of getDownloadURL which includes expiring tokens,
    // construct a clean URL to match entity image behaviors
    const downloadURL = `https://storage.googleapis.com/${snapshot.ref.bucket}/${storagePath}`;

    return {
        url: downloadURL,
        path: storagePath,
    };
  } catch (error) {
    console.error('Error uploading image to Firebase Storage:', error);
    // Depending on requirements, you might want to re-throw or handle differently
    throw error; // Re-throw the original Firebase error for better debugging
  }
}

/**
 * Deletes a file from Firebase Storage.
 *
 * @param storage - The FirebaseStorage instance.
 * @param path - The path of the file to delete.
 */
export async function deleteStorageFile(storage: FirebaseStorage, path: string): Promise<void> {
  if (!path) return;
  const storageRef = ref(storage, path);
  try {
    await deleteObject(storageRef);
  } catch (error) {
    console.warn(`⚠️ Failed to delete storage file at ${path}:`, error);
  }
}

/**
 * Deletes all variants of a ProcessedImage from Firebase Storage.
 *
 * @param storage - The FirebaseStorage instance.
 * @param image - The ProcessedImage to delete.
 */
export async function deleteProcessedImage(storage: FirebaseStorage, image: ProcessedImage): Promise<void> {
  if (!image) return;
  const paths = [
    image.original?.path,
    image.profile?.path,
    image.miniature?.path,
  ].filter(Boolean) as string[];

  await Promise.all(paths.map(path => deleteStorageFile(storage, path)));
}
